// useBrandConfig — owner-side hook for the white-label `brand_configs` row.
//
// v1 is single-tenant-per-owner (unique(owner_id) in the migration), so the
// hook exposes a single optional row + upsert mutation + logo upload helper.
// Studio-tier gating happens in the UI layer (BrandingTab); RLS guarantees a
// non-Studio user can't read another owner's config even if a bug let them
// reach the hook.

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface BrandConfigRow {
  id: string;
  owner_id: string;
  logo_url: string | null;
  primary_color: string | null;
  accent_color: string | null;
  custom_domain: string | null;
  domain_status: 'unverified' | 'verified' | 'disabled';
  created_at: string;
  updated_at: string;
}

export interface UpsertBrandConfigInput {
  logoUrl?: string | null;
  primaryColor?: string | null;
  accentColor?: string | null;
  customDomain?: string | null;
}

async function fetchOwnerId(): Promise<string | null> {
  const { data } = await supabase.auth.getUser();
  return data?.user?.id ?? null;
}

async function fetchBrandConfig(ownerId: string): Promise<BrandConfigRow | null> {
  const { data, error } = await supabase
    .from('brand_configs')
    .select(
      'id, owner_id, logo_url, primary_color, accent_color, custom_domain, domain_status, created_at, updated_at',
    )
    .eq('owner_id', ownerId)
    .maybeSingle();
  if (error) throw error;
  return (data as BrandConfigRow | null) ?? null;
}

export function useBrandConfig() {
  const queryClient = useQueryClient();

  const ownerQuery = useQuery({
    queryKey: ['brand-config', 'owner-id'],
    queryFn: fetchOwnerId,
    staleTime: 5 * 60_000,
  });

  const ownerId = ownerQuery.data ?? null;

  const configQuery = useQuery({
    queryKey: ['brand-config', ownerId],
    queryFn: () => (ownerId ? fetchBrandConfig(ownerId) : Promise.resolve(null)),
    enabled: !!ownerId,
  });

  const upsert = useMutation({
    mutationFn: async (input: UpsertBrandConfigInput) => {
      const id = ownerId ?? (await fetchOwnerId());
      if (!id) throw new Error('Not authenticated');
      const payload = {
        owner_id: id,
        updated_at: new Date().toISOString(),
        ...(input.logoUrl !== undefined ? { logo_url: input.logoUrl } : {}),
        ...(input.primaryColor !== undefined ? { primary_color: input.primaryColor } : {}),
        ...(input.accentColor !== undefined ? { accent_color: input.accentColor } : {}),
        ...(input.customDomain !== undefined ? { custom_domain: input.customDomain } : {}),
      };

      const { data, error } = await supabase
        .from('brand_configs')
        .upsert(payload, { onConflict: 'owner_id' })
        .select(
          'id, owner_id, logo_url, primary_color, accent_color, custom_domain, domain_status, created_at, updated_at',
        )
        .single();
      if (error) throw error;
      return data as BrandConfigRow;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['brand-config', ownerId] });
      // Effective brand reads from this same source — invalidate it too so
      // the page picks up the new colors / logo without a reload.
      queryClient.invalidateQueries({ queryKey: ['effective-brand'] });
    },
  });

  // Logo upload — writes to private `brand-logos` bucket under owner-scoped
  // path. Returns the storage path (NOT a signed URL); callers persist that
  // path via `upsert({ logoUrl })` and the renderer creates signed URLs at
  // display time.
  const uploadLogo = useMutation({
    mutationFn: async (file: File) => {
      const id = ownerId ?? (await fetchOwnerId());
      if (!id) throw new Error('Not authenticated');
      if (file.size > 1024 * 1024) {
        throw new Error('Logo must be 1 MB or smaller.');
      }
      if (!file.type.startsWith('image/')) {
        throw new Error('Logo must be an image.');
      }
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const path = `${id}/${Date.now()}-${safeName}`;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await (supabase as any).storage
        .from('brand-logos')
        .upload(path, file, { upsert: false, contentType: file.type });
      if (error) throw error;
      return path;
    },
  });

  return {
    config: configQuery.data ?? null,
    isLoading: ownerQuery.isLoading || configQuery.isLoading,
    error: configQuery.error,
    upsert,
    uploadLogo,
  };
}
